import { useState } from "react";
import { useMutation, useApolloClient } from "@apollo/client/react";
import { useTranslations } from "@/common/hooks/use-translations";
import {
  GetLedgerEntryContextDocument,
  DeleteLedgerEntrySourceSliceDocument,
} from "@/graphql/definitions";
import { useLedgerWriteRefresh } from "@/common/hooks/use-apollo-cache";
import { useErrorMessage } from "@/common/lib/errors/error-message";
import { Alert, AlertDescription } from "@/common/components/ui/alert";
import { Button } from "@/common/components/ui/button";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/common/components/ui/alert-dialog";
import { Loader2 } from "lucide-react";
import { restoreFocusOnDialogClose } from "@/common/lib/focus/restore-focus-on-dialog-close";
import { usePendingAction } from "@/common/hooks/use-pending-action";
import type { RefObject } from "react";
import type { BudgetGroup } from "./types";

interface DeleteBudgetDialogProps {
  target: BudgetGroup | null;
  ledgerId: string;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  returnFocusRef?: RefObject<HTMLElement | null>;
  fallbackFocusRef?: RefObject<HTMLElement | null>;
}

const DELETE_INTERVAL_KEYS: Record<string, string> = {
  daily: "page.budget.budgetDeleteIntervalDaily",
  weekly: "page.budget.budgetDeleteIntervalWeekly",
  monthly: "page.budget.budgetDeleteIntervalMonthly",
  quarterly: "page.budget.budgetDeleteIntervalQuarterly",
  yearly: "page.budget.budgetDeleteIntervalYearly",
};

export function DeleteBudgetDialog({
  target,
  ledgerId,
  onOpenChange,
  onSuccess,
  returnFocusRef,
  fallbackFocusRef,
}: DeleteBudgetDialogProps) {
  // Failed source-read/deletion returns focus to Delete for the retry,
  // unless the reader deliberately moved elsewhere (w4/1082).
  const { run, pending: isDeleting } = usePendingAction();
  // Keyed to the directive it is about, so it never shows for another row.
  const [failure, setFailure] = useState<{
    entryHash: string;
    message: string;
  } | null>(null);
  const { t } = useTranslations();
  const deleteIntervalLabel = (interval: string) => {
    const key = DELETE_INTERVAL_KEYS[interval.toLowerCase()];
    return key ? t(key) : interval;
  };
  const formatError = useErrorMessage();
  const client = useApolloClient();
  const clearCache = useLedgerWriteRefresh();
  const [deleteMutation] = useMutation(DeleteLedgerEntrySourceSliceDocument);

  const doDelete = async (): Promise<boolean | void> => {
    if (!target) return;
    const { entry_hash: entryHash } = target;
    setFailure(null);
    try {
      const { data } = await client.query({
        query: GetLedgerEntryContextDocument,
        variables: { entryHash: target.entry_hash, ledgerId },
        fetchPolicy: "network-only",
      });
      const sha256sum = data?.getLedgerEntryContext?.sha256sum;
      if (!sha256sum) {
        setFailure({ entryHash, message: t("common.errors.generic") });
        return;
      }

      await deleteMutation({
        variables: {
          ledgerId,
          input: { entryHash: target.entry_hash, sha256sum },
        },
      });
      onOpenChange(false);
      clearCache();
      onSuccess();
      return true;
    } catch (err) {
      // The dialog stays open on its target, with Cancel and Delete to retry.
      setFailure({ entryHash, message: formatError(err) });
      return undefined;
    }
  };

  const handleDelete = () => {
    void run(doDelete);
  };

  return (
    <AlertDialog
      open={target !== null}
      onOpenChange={(v) => {
        if (!v && !isDeleting) onOpenChange(v);
      }}
    >
      <AlertDialogContent
        onCloseAutoFocus={(event) => {
          restoreFocusOnDialogClose(
            event,
            returnFocusRef?.current,
            fallbackFocusRef?.current,
          );
        }}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t("page.budget.budgetDeleteTitle")}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {target &&
              t("page.budget.budgetDeleteDescription", {
                // The confirmation sentence carries its own localized
                // cadence phrase; the stored token stays a fallback for an
                // unknown cadence (w4/1081).
                interval: deleteIntervalLabel(target.interval),
                amount: target.amount,
                account: target.account,
                date: target.date ?? "",
              })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {failure && failure.entryHash === target?.entry_hash && (
          <Alert variant="destructive">
            <AlertDescription>{failure.message}</AlertDescription>
          </Alert>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>
            {t("common.cancel")}
          </AlertDialogCancel>
          <Button
            variant="destructive"
            onClick={() => void handleDelete()}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                {t("page.budget.budgetDeleting")}
              </>
            ) : (
              t("common.delete")
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
